import type { AbortTripInput } from '@chukua/common';
import { useCallback, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { ApiError } from '../../../shared/shared.api';
import { abortTrip } from '../dispatch.api';

/** The server's own words for a refusal (already aborted, not yet dispatched); anything else is a generic failure. */
function refusalMessage(err: unknown): string {
  const body = err instanceof ApiError ? err.body : null;
  const message = typeof body === 'object' && body !== null && 'message' in body ? (body as { message: unknown }).message : null;
  return typeof message === 'string' ? message : 'Could not abort the trip. Try again.';
}

/** Aborting one trip. A refusal stays in the modal as `error`; only a success toasts. */
export function useAbortTrip(tripId: string, onAborted: () => void) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const abort = useCallback(
    async (values: AbortTripInput): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const trip = await abortTrip(tripId, values);
        toast.success(`Trip ${trip.number} aborted`);
        onAborted();
        return true;
      } catch (err) {
        setError(refusalMessage(err));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tripId, toast, onAborted],
  );

  return { abort, isSubmitting, error, clearError: () => setError(null) };
}
